// Informe ejecutivo SEMANAL descargable con marca Jidokap (solicitud del
// cliente 2026-07-05 — plantilla "Informe Ejecutivo de Producción").
//
// Mismo mecanismo de dibujo que el resumen de turno del supervisor
// (resumenImagen.ts): canvas nativo, cero dependencias. La salida es un PDF
// MULTIPÁGINA (lib/pdf.ts, también sin dependencias): página 1 consolidada +
// una página de detalle por planta con su estado. SIEMPRE en paleta clara:
// es un documento para compartir/imprimir, no una vista de pantalla.
//
// Diferencias deliberadas con la plantilla del cliente:
//   - En vez de "OEE" van DISPONIBILIDAD y RENDIMIENTO por separado (decisión
//     del responsable 2026-07-05): la plataforma no captura el eje de calidad
//     y un OEE incompleto invita a comparaciones injustas.
//   - "Observaciones gerenciales" = HECHOS calculables, nunca recomendaciones
//     inventadas (misma regla que "Dónde apoyar").
//
// El llamador arma InformeDatos con los payloads que la página ya tiene
// (resumen semanal + líneas + detalle por día/hora) — acá solo se dibuja.

import { descargarPdf } from '../../lib/pdf';
// Primitivas de dibujo compartidas con el Informe Gerencial del coordinador
// (extraídas a lib/informeCanvas.ts — 2026-09). La paleta P y los formatters
// son EXACTAMENTE los que vivían acá.
import {
  cargarFuentes,
  copM,
  drawBarrasDuales,
  drawBarrasH,
  drawCardSeccion,
  drawDona,
  drawLinea,
  drawTendencia,
  drawWordmark,
  horasFmt,
  miles,
  MONO,
  P,
  pctFmt,
  roundRect,
  SANS,
  SERIF,
} from '../../lib/informeCanvas';

export interface InformeDatos {
  semanaLabel: string;
  rangoLabel: string;
  plantasLabel: string;
  /** Título de la página: 'Consolidado' o 'Planta Sopó'. */
  titulo: string;
  /** Estado de la planta (solo páginas de detalle). */
  estado: { label: string; tone: 'ok' | 'warn' | 'crit' | 'neutral' } | null;
  kpis: {
    programada: number | null;
    real: number;
    cumplimiento_pct: number | null;
    rendimiento_pct: number | null;
    disponibilidad_pct: number | null;
    paro_min: number;
    valor_cop: number | null;
    valor_parcial: boolean;
  };
  /** Finanzas del período ($ null sin puede_ver_costos). */
  finanzas: {
    valor_cop: number | null;
    insumos_cop: number | null;
    costo_mo_cop: number | null;
    utilidad_cop: number | null;
    perdida_paros_cop: number | null;
  };
  /** Producción programada vs real por día de la semana. */
  porDia: { label: string; programada: number | null; real: number }[];
  /** Perfil de producción por hora del día ('00'..'23'). */
  porHora: { label: string; unidades: number }[];
  /** Top causas de paro (madre) de la semana. */
  causas: { nombre: string; minutos: number }[];
  /** Distribución del tiempo trabajado. */
  tiempo: { productivasH: number; paroH: number };
  /** Tendencia de cumplimiento (últimas semanas, una línea por planta). */
  tendencia: {
    plantas: string[];
    semanas: { label: string; valores: (number | null)[] }[];
  };
  observaciones: string[];
  descargadoPor: string;
}

/** Dibuja UNA página del informe y devuelve su canvas. */
function dibujarPagina(d: InformeDatos): HTMLCanvasElement {
  const scale = 2;
  const canvasW = 1040;
  const padX = 44;
  const contentW = canvasW - padX * 2;

  // ── Layout vertical ──
  const topBarH = 64;
  const tituloY = topBarH + 66;
  const metaY = tituloY + 32;
  const kpiY = metaY + 26;
  const kpiFilaH = 40;
  const kpiFilas = [
    [
      'Producción programada',
      d.kpis.programada !== null ? `${miles(d.kpis.programada)} paq` : 'n/s',
    ],
    ['Producción real', `${miles(d.kpis.real)} paq`],
    ['Cumplimiento del programa', pctFmt(d.kpis.cumplimiento_pct)],
    ['Rendimiento vs capacidad', pctFmt(d.kpis.rendimiento_pct)],
    ['Disponibilidad', pctFmt(d.kpis.disponibilidad_pct)],
    ['Tiempo de paro', horasFmt(d.kpis.paro_min)],
    ...(d.kpis.valor_cop !== null
      ? ([
          ['Valor producido', `${copM(d.kpis.valor_cop)}${d.kpis.valor_parcial ? ' *' : ''}`],
        ] as const)
      : []),
  ] as const;
  const kpiH = 34 + kpiFilaH * kpiFilas.length + 14;

  const finFilas = [
    ...(d.finanzas.valor_cop !== null
      ? ([
          ['Valor producido', `${copM(d.finanzas.valor_cop)}${d.kpis.valor_parcial ? ' *' : ''}`],
        ] as const)
      : []),
    ...(d.finanzas.insumos_cop !== null && d.finanzas.insumos_cop > 0
      ? ([['Insumos cobrados (tiendas)', copM(d.finanzas.insumos_cop)]] as const)
      : []),
    ...(d.finanzas.costo_mo_cop !== null
      ? ([['Costo de mano de obra', copM(d.finanzas.costo_mo_cop)]] as const)
      : []),
    ...(d.finanzas.utilidad_cop !== null
      ? ([['Utilidad estimada', copM(d.finanzas.utilidad_cop)]] as const)
      : []),
    ...(d.finanzas.perdida_paros_cop !== null
      ? ([['Pérdida por paros (valor no producido)', copM(d.finanzas.perdida_paros_cop)]] as const)
      : []),
  ] as const;
  const finY = kpiY + kpiH + 18;
  const finH = finFilas.length > 0 ? 34 + 40 * finFilas.length + 14 : 0;

  const chartH = 236;
  const gap = 18;
  const halfW = (contentW - gap) / 2;
  const fila1Y = finY + finH + (finFilas.length > 0 ? gap : 0);
  const fila2Y = fila1Y + chartH + gap;
  const tendY = fila2Y + chartH + gap;
  const tendH = 240;
  const obsY = tendY + tendH + gap;
  const obsH = 46 + d.observaciones.length * 26 + (d.kpis.valor_parcial ? 24 : 0) + 12;
  const footY = obsY + obsH + 24;
  const footH = 58;
  const canvasH = footY + footH;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW * scale;
  canvas.height = canvasH * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  ctx.textBaseline = 'middle';

  // ── Fondo + barra superior ──
  ctx.fillStyle = P.pageBg;
  ctx.fillRect(0, 0, canvasW, canvasH);
  ctx.fillStyle = P.surface;
  ctx.fillRect(0, 0, canvasW, topBarH);
  ctx.strokeStyle = P.border;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, topBarH + 0.5);
  ctx.lineTo(canvasW, topBarH + 0.5);
  ctx.stroke();
  const wmW = drawWordmark(ctx, padX, topBarH / 2, 24, P.text);
  ctx.strokeStyle = P.border;
  ctx.beginPath();
  ctx.moveTo(padX + wmW + 16, topBarH / 2 - 12);
  ctx.lineTo(padX + wmW + 16, topBarH / 2 + 12);
  ctx.stroke();
  ctx.font = `14px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.textAlign = 'left';
  ctx.fillText(d.plantasLabel, padX + wmW + 30, topBarH / 2);
  // Badge semana
  ctx.font = `600 11px ${SANS}`;
  const badge = d.semanaLabel.toUpperCase();
  const bw = ctx.measureText(badge).width + 36;
  roundRect(ctx, canvasW - padX - bw, topBarH / 2 - 14, bw, 28, 14);
  ctx.strokeStyle = P.border;
  ctx.stroke();
  ctx.fillStyle = P.text;
  ctx.fillText(badge, canvasW - padX - bw + 18, topBarH / 2 + 0.5);

  // ── Título ──
  ctx.font = `600 11px ${SANS}`;
  ctx.fillStyle = P.accent;
  ctx.fillText('INFORME EJECUTIVO DE PRODUCCIÓN', padX, topBarH + 34);
  ctx.font = `34px ${SERIF}`;
  ctx.fillStyle = P.text;
  const tituloTexto = `${d.titulo} — ${d.rangoLabel}`;
  ctx.fillText(tituloTexto, padX, tituloY);
  if (d.estado) {
    const tw = ctx.measureText(tituloTexto).width;
    const toneColor =
      d.estado.tone === 'ok'
        ? P.ok
        : d.estado.tone === 'warn'
          ? P.warn
          : d.estado.tone === 'crit'
            ? P.crit
            : P.faint;
    ctx.font = `600 11px ${SANS}`;
    const ew = ctx.measureText(d.estado.label.toUpperCase()).width + 34;
    roundRect(ctx, padX + tw + 16, tituloY - 13, ew, 26, 13);
    ctx.strokeStyle = P.border;
    ctx.stroke();
    ctx.fillStyle = toneColor;
    ctx.beginPath();
    ctx.arc(padX + tw + 16 + 13, tituloY, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.text;
    ctx.fillText(d.estado.label.toUpperCase(), padX + tw + 16 + 23, tituloY + 0.5);
  }
  ctx.font = `13px ${SANS}`;
  ctx.fillStyle = P.muted;
  const generado = new Date().toLocaleString('es-CO', { dateStyle: 'long' });
  ctx.fillText(`${d.plantasLabel} · generado el ${generado}`, padX, metaY);

  // ── Tabla de KPIs ──
  {
    ctx.fillStyle = P.surface;
    roundRect(ctx, padX, kpiY, contentW, kpiH, 8);
    ctx.fill();
    ctx.strokeStyle = P.border;
    ctx.stroke();
    ctx.font = `600 12px ${SANS}`;
    ctx.fillStyle = P.text;
    ctx.fillText('Resumen ejecutivo', padX + 18, kpiY + 22);
    const filaY0 = kpiY + 40;
    kpiFilas.forEach(([label, valor], i) => {
      const y = filaY0 + kpiFilaH * i;
      if (i % 2 === 0) {
        ctx.fillStyle = P.zebra;
        ctx.fillRect(padX + 12, y, contentW - 24, kpiFilaH);
      }
      ctx.font = `13px ${SANS}`;
      ctx.fillStyle = P.muted;
      ctx.textAlign = 'left';
      ctx.fillText(label, padX + 24, y + kpiFilaH / 2);
      ctx.font = `500 15px ${MONO}`;
      ctx.fillStyle = P.text;
      ctx.textAlign = 'right';
      ctx.fillText(valor, padX + contentW - 24, y + kpiFilaH / 2);
      ctx.textAlign = 'left';
    });
  }

  // ── Finanzas de la semana ──
  if (finFilas.length > 0) {
    ctx.fillStyle = P.surface;
    roundRect(ctx, padX, finY, contentW, finH, 8);
    ctx.fill();
    ctx.strokeStyle = P.border;
    ctx.stroke();
    ctx.font = `600 12px ${SANS}`;
    ctx.fillStyle = P.text;
    ctx.textAlign = 'left';
    ctx.fillText('Finanzas del período', padX + 18, finY + 22);
    const filaY0 = finY + 40;
    finFilas.forEach(([label, valor], i) => {
      const y = filaY0 + 40 * i;
      if (i % 2 === 0) {
        ctx.fillStyle = P.zebra;
        ctx.fillRect(padX + 12, y, contentW - 24, 40);
      }
      ctx.font = `13px ${SANS}`;
      ctx.fillStyle = P.muted;
      ctx.textAlign = 'left';
      ctx.fillText(label, padX + 24, y + 20);
      ctx.font = `500 15px ${MONO}`;
      // Utilidad negativa y pérdida por paros en rojo (señal, no decoración).
      const esNegativo = valor.startsWith('$-');
      const esPerdida = label.startsWith('Pérdida');
      ctx.fillStyle = esNegativo || esPerdida ? P.crit : P.text;
      ctx.textAlign = 'right';
      ctx.fillText(valor, padX + contentW - 24, y + 20);
      ctx.textAlign = 'left';
    });
  }

  // ── Gráficas ──
  drawBarrasDuales(
    ctx,
    drawCardSeccion(ctx, padX, fila1Y, halfW, chartH, 'Producción programada vs real · por día'),
    d.porDia,
  );
  drawLinea(
    ctx,
    drawCardSeccion(ctx, padX + halfW + gap, fila1Y, halfW, chartH, 'Producción por hora del día'),
    d.porHora.map((h) => ({ label: h.label, valor: h.unidades })),
    P.info,
  );
  drawBarrasH(
    ctx,
    drawCardSeccion(ctx, padX, fila2Y, halfW, chartH, 'Principales causas de paro'),
    d.causas.slice(0, 5),
  );
  drawDona(
    ctx,
    drawCardSeccion(ctx, padX + halfW + gap, fila2Y, halfW, chartH, 'Distribución del tiempo'),
    d.tiempo,
  );
  drawTendencia(
    ctx,
    drawCardSeccion(
      ctx,
      padX,
      tendY,
      contentW,
      tendH,
      'Tendencia de cumplimiento · últimas semanas',
    ),
    d.tendencia,
  );

  // ── Observaciones gerenciales (hechos) ──
  {
    ctx.fillStyle = P.surface;
    roundRect(ctx, padX, obsY, contentW, obsH, 8);
    ctx.fill();
    ctx.strokeStyle = P.border;
    ctx.stroke();
    ctx.font = `600 12px ${SANS}`;
    ctx.fillStyle = P.text;
    ctx.textAlign = 'left';
    ctx.fillText('Observaciones gerenciales', padX + 18, obsY + 24);
    ctx.font = `13px ${SANS}`;
    d.observaciones.forEach((obs, i) => {
      const y = obsY + 52 + i * 26;
      ctx.fillStyle = P.accent;
      ctx.fillText('•', padX + 22, y);
      ctx.fillStyle = P.muted;
      ctx.fillText(obs, padX + 38, y);
    });
    if (d.kpis.valor_parcial) {
      ctx.font = `11px ${SANS}`;
      ctx.fillStyle = P.faint;
      ctx.fillText(
        '* Valor parcial: hay SKUs sin precio cargado — esas unidades entran como $0.',
        padX + 22,
        obsY + 52 + d.observaciones.length * 26 + 6,
      );
    }
  }

  // ── Pie ──
  ctx.fillStyle = P.surface;
  ctx.fillRect(0, footY, canvasW, footH);
  ctx.strokeStyle = P.border;
  ctx.beginPath();
  ctx.moveTo(0, footY + 0.5);
  ctx.lineTo(canvasW, footY + 0.5);
  ctx.stroke();
  const footMidY = footY + footH / 2;
  ctx.textAlign = 'left';
  ctx.font = `600 13px ${SANS}`;
  ctx.fillStyle = P.ok;
  ctx.fillText('✓', padX, footMidY);
  ctx.fillStyle = P.text;
  ctx.fillText('Solo información validada', padX + 18, footMidY);
  let fx = padX + 18 + ctx.measureText('Solo información validada').width + 22;
  const generadoHora = new Date().toLocaleString('es-CO', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  ctx.font = `13px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.fillText('Descargado por ', fx, footMidY);
  fx += ctx.measureText('Descargado por ').width;
  ctx.font = `600 13px ${SANS}`;
  ctx.fillStyle = P.text;
  ctx.fillText(d.descargadoPor, fx, footMidY);
  fx += ctx.measureText(d.descargadoPor).width;
  ctx.font = `13px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.fillText(` · Directivo · ${generadoHora}`, fx, footMidY);
  ctx.font = `15px ${SERIF}`;
  const wmSmallW = ctx.measureText('Jidokap.').width;
  drawWordmark(ctx, canvasW - padX - wmSmallW, footMidY, 15, P.muted);

  return canvas;
}

/**
 * Genera el PDF multipágina del informe (página 1 consolidada + una por
 * planta) y dispara la descarga. Cada página se dibuja en canvas y se
 * empaqueta como JPEG vía lib/pdf.ts (sin dependencias).
 */
export async function descargarInformeEjecutivo(paginas: InformeDatos[]): Promise<void> {
  await cargarFuentes();
  const jpegs = paginas.map((d) => {
    const canvas = dibujarPagina(d);
    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.92),
      // MediaBox en px CSS (el canvas interno va a scale 2 → Width/Height ×2).
      w: canvas.width / 2,
      h: canvas.height / 2,
    };
  });
  const nombre = paginas[0]
    ? `informe-ejecutivo-${paginas[0].semanaLabel.toLowerCase().replace(/\s+/g, '-')}.pdf`
    : 'informe-ejecutivo.pdf';
  descargarPdf(jpegs, nombre);
}
